import { useCallback, useEffect, useRef } from 'react';
import type { PlayerEvent } from '@mlm/shared';
import type { PlayerSnapshot } from '@mlm/ui';
import {
  decideHeartbeat,
  initialHeartbeatState,
  suppressAfterThrottle,
  HEARTBEAT_INTERVAL_MS,
  SEEK_QUIET_MS,
  type HeartbeatEventKind,
  type HeartbeatState,
} from '@mlm/shared';
import { flushLessonPositionKeepalive, writeLessonPosition } from '../lib/player/heartbeat-client';
import type { AdapterEventMeta } from './lesson-player';

/**
 * Persists the playhead so a learner resumes where they stopped.
 *
 * All the "when" lives in `decideHeartbeat`; this is the wiring. Two details
 * worth keeping: the write never touches the react-query cache (a `setQueryData`
 * every ten seconds would re-render the whole player page around a playing
 * video and re-create every prop-derived timer under it), and it listens on
 * `blur` as well as `visibilitychange`/`pagehide` because in the Capacitor
 * Android WebView backgrounding the app never unloads the document — `pagehide`
 * simply does not fire there.
 */
export function useLessonHeartbeat({
  courseId,
  lessonId,
  guest,
}: {
  courseId: string;
  lessonId: string | null;
  guest: boolean;
}) {
  const stateRef = useRef<HeartbeatState>(initialHeartbeatState());
  const latest = useRef({
    position: 0,
    playing: false,
    resumed: false,
    refreshing: false,
    /** The seek policy's mark, so every write keeps the server maximum current. */
    maxWatched: 0,
  });
  const seekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Captured in the closure so a lesson switch reports the OLD lesson's time.
  const lessonRef = useRef(lessonId);
  const guestRef = useRef(guest);
  guestRef.current = guest;

  const fire = useCallback(
    (kind: HeartbeatEventKind) => {
      const targetLesson = lessonRef.current;
      if (!targetLesson) return;
      const snap = latest.current;
      const decision = decideHeartbeat(stateRef.current, {
        kind,
        position: snap.position,
        now: Date.now(),
        playing: snap.playing,
        guest: guestRef.current,
        resumed: snap.resumed,
        refreshing: snap.refreshing,
        maxPosition: snap.maxWatched,
      });
      stateRef.current = decision.state;
      if (!decision.send) return;
      const extra = { maxPositionSeconds: decision.maxPosition, ended: decision.ended };
      if (decision.transport === 'keepalive') {
        flushLessonPositionKeepalive(courseId, targetLesson, decision.position, extra);
        return;
      }
      void writeLessonPosition(courseId, targetLesson, decision.position, extra).then((result) => {
        // A 429 means we are writing too often: stand down rather than toast.
        if (!result.ok && result.status === 429) {
          stateRef.current = suppressAfterThrottle(stateRef.current, Date.now());
        }
      });
    },
    [courseId],
  );

  // Flush the outgoing lesson before the refs are re-pointed at the new one.
  useEffect(() => {
    const previous = lessonRef.current;
    if (previous && previous !== lessonId) fire('lessonSwitch');
    lessonRef.current = lessonId;
    stateRef.current = initialHeartbeatState();
    latest.current = {
      position: 0,
      playing: false,
      resumed: false,
      refreshing: false,
      maxWatched: 0,
    };
  }, [lessonId, fire]);

  const onAdapterEvent = useCallback(
    (event: PlayerEvent, snapshot: PlayerSnapshot, meta: AdapterEventMeta) => {
      latest.current = {
        position: snapshot.currentTime,
        playing: snapshot.state === 'playing',
        resumed: meta.resumed,
        refreshing: meta.refreshing,
        maxWatched: meta.maxWatched ?? latest.current.maxWatched,
      };
      if (event.type === 'state' && event.state === 'ended') {
        fire('ended');
        return;
      }
      if (event.type === 'state' && event.state === 'paused') {
        fire('pause');
        return;
      }
      if (event.type === 'seeked') {
        // Coalesce: a held arrow key repeats ~30x a second and each repeat
        // lands a seek. One write goes out when the learner stops moving.
        if (seekTimer.current) clearTimeout(seekTimer.current);
        seekTimer.current = setTimeout(() => fire('seekQuiet'), SEEK_QUIET_MS);
      }
    },
    [fire],
  );

  useEffect(() => {
    const tick = setInterval(() => fire('tick'), HEARTBEAT_INTERVAL_MS);
    const onVisibility = () => document.visibilityState === 'hidden' && fire('hidden');
    const onBlur = () => fire('blur');
    const onPageHide = () => fire('pagehide');
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      clearInterval(tick);
      if (seekTimer.current) clearTimeout(seekTimer.current);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [fire]);

  // Last write of the session: the component is going away with the position
  // still only in memory.
  useEffect(
    () => () => {
      fire('lessonSwitch');
    },
    [fire],
  );

  return { onAdapterEvent, lastPosition: latest };
}
